import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { getHoverColour } from "@/lib/brand/hoverColour";
import { supabase } from "@/integrations/supabase/client";
import { resolveBrandTarget } from "@/lib/brand/resolveSubdomain";
import {
  ensureClashGrotesk,
  getFontStack,
  getSupportedBrandFont,
  getSupportedBrandFontWeight,
  isGoogleBrandFont,
} from "@/lib/brand/fontCatalog";

export interface BrandConfig {
  company_name?: string;
  logo_url?: string;
  logo_scale?: number;
  logo_dark_url?: string;
  favicon_url?: string;
  support_email?: string;
  color_tokens?: Record<string, string>; // e.g. { primary: "71 73% 76%" }
  font_config?: { heading?: string; body?: string; heading_weight?: string | number; body_weight?: string | number };
  landing_content?: Record<string, unknown>;
}

export interface Brand {
  subdomain: string;
  organization_id?: string | null;
  allow_indexing?: boolean;
  config: BrandConfig;
}

interface BrandState {
  brand: Brand | null;
  isWhiteLabel: boolean;
  isPreview: boolean;
  loading: boolean;
}

const BrandContext = createContext<BrandState>({ brand: null, isWhiteLabel: false, isPreview: false, loading: false });
export const useBrand = () => useContext(BrandContext);

const appliedBrandProperties = new Set<string>();

function setBrandProperty(root: HTMLElement, property: string, value: string) {
  root.style.setProperty(property, value);
  appliedBrandProperties.add(property);
}

function applyBrand(config: BrandConfig) {
  const root = document.documentElement;
  root.dataset.whiteLabel = "true";
  const colors = config.color_tokens || {};
  Object.entries(colors).forEach(([k, v]) => {
    if (typeof v === "string" && v.trim()) setBrandProperty(root, `--${k.replace(/^--/, "")}`, v);
  });
  const brandMain = colors.primary || "335 41% 14%";
  const brandAccent = colors.accent || "71 73% 76%";
  const brandBackground = colors.background || brandMain;
  const brandForeground = colors.foreground || colors["primary-foreground"] || "0 0% 96%";
  const brandHeadingForeground = colors["heading-foreground"] || brandForeground;
  const brandSubheadingForeground = colors["subheading-foreground"] || brandForeground;
  setBrandProperty(root, "--brand-main", brandMain);
  setBrandProperty(root, "--brand-accent", brandAccent);
  setBrandProperty(root, "--brand-background", brandBackground);
  setBrandProperty(root, "--brand-foreground", brandForeground);
  setBrandProperty(root, "--brand-heading-foreground", brandHeadingForeground);
  setBrandProperty(root, "--brand-subheading-foreground", brandSubheadingForeground);
  setBrandProperty(root, "--brand-subheading-opacity", colors["subheading-foreground"] ? "1" : "0.68");
  const brandSurface = colors.background || "220 13% 18%";
  const brandActionForeground = colors["primary-foreground"] || "0 0% 10%";
  setBrandProperty(root, "--brand-surface", brandSurface);
  setBrandProperty(root, "--brand-action", brandMain);
  setBrandProperty(root, "--brand-action-hover", getHoverColour(brandMain, colors["primary-hover"]));
  setBrandProperty(root, "--brand-action-foreground", brandActionForeground);
  setBrandProperty(root, "--card", brandSurface);
  setBrandProperty(root, "--popover", brandSurface);
  setBrandProperty(root, "--surface-background", brandSurface);
  setBrandProperty(root, "--card-foreground", brandForeground);
  setBrandProperty(root, "--popover-foreground", brandForeground);
  setBrandProperty(root, "--sidebar-primary", brandMain);
  setBrandProperty(root, "--sidebar-primary-foreground", brandActionForeground);
  setBrandProperty(root, "--ring", brandAccent);
  setBrandProperty(root, "--icon-background", brandMain);
  setBrandProperty(root, "--icon-foreground", brandActionForeground);
  // Derive dialog, table, tab and input tokens from the page background so shared components follow the brand.
  const m = brandSurface.match(/^\s*([\d.]+)\s+([\d.]+)%\s+([\d.]+)%\s*$/);
  if (m) {
    const [h, s, l] = [Number(m[1]), Number(m[2]), Number(m[3])];
    const dark = l < 50;
    const shade = (d: number) => `${h} ${s}% ${Math.max(0, Math.min(100, dark ? l + d : l - d))}%`;
    const fg = brandForeground.trim().split(/\s+/);
    setBrandProperty(root, "--muted", shade(6));
    setBrandProperty(root, "--secondary", shade(9));
    setBrandProperty(root, "--border", shade(16));
    setBrandProperty(root, "--input", shade(20));
    setBrandProperty(root, "--muted-foreground", fg.length === 3 ? `${fg[0]} ${fg[1]} ${dark ? 70 : 40}%` : brandForeground);
    setBrandProperty(root, "--secondary-foreground", brandForeground);
    setBrandProperty(root, "--accent-foreground", brandActionForeground);
    setBrandProperty(root, "--sidebar-border", shade(16));
  }
  const headingFont = getSupportedBrandFont(config.font_config?.heading);
  const bodyFont = getSupportedBrandFont(config.font_config?.body);
  const headingWeight = getSupportedBrandFontWeight(config.font_config?.heading_weight, "700");
  const bodyWeight = getSupportedBrandFontWeight(config.font_config?.body_weight, "400");
  const fonts = Array.from(new Set([headingFont, bodyFont].filter((font): font is string => !!font)));

  document.getElementById("brand-google-fonts")?.remove();
  root.style.removeProperty("--brand-heading-font");
  root.style.removeProperty("--brand-body-font");

  if (fonts.includes("Clash Grotesk")) ensureClashGrotesk();

  const hostedFonts = fonts.filter(isGoogleBrandFont);
  if (hostedFonts.length) {
    const link = document.createElement("link");
    link.id = "brand-google-fonts";
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?${hostedFonts.map((font) => `family=${encodeURIComponent(font)}:wght@400;500;600;700`).join("&")}&display=swap`;
    document.head.appendChild(link);
  }

  const headingStack = getFontStack(headingFont);
  const bodyStack = getFontStack(bodyFont);
  if (headingStack) setBrandProperty(root, "--brand-heading-font", headingStack);
  if (bodyStack) setBrandProperty(root, "--brand-body-font", bodyStack);
  setBrandProperty(root, "--brand-heading-weight", headingWeight);
  setBrandProperty(root, "--brand-body-weight", bodyWeight);
  if (config.favicon_url) {
    let icon = document.querySelector<HTMLLinkElement>("link[rel='icon']");
    if (!icon) { icon = document.createElement("link"); icon.rel = "icon"; document.head.appendChild(icon); }
    icon.href = config.favicon_url;
  }
}

function resetBrand() {
  const root = document.documentElement;
  delete root.dataset.whiteLabel;
  appliedBrandProperties.forEach((property) => root.style.removeProperty(property));
  appliedBrandProperties.clear();
  document.getElementById("brand-google-fonts")?.remove();
}

export function BrandProvider({ children }: { children: ReactNode }) {
  const [target] = useState(resolveBrandTarget);
  const [state, setState] = useState<BrandState>({ brand: null, isWhiteLabel: false, isPreview: !!target?.isPreview, loading: !!target });

  useEffect(() => {
    if (!target) return;
    let cancelled = false;
    (async () => {
      let brand: Brand | null = null;
      try {
        if (target.wantsDraft) {
          // Only superusers can read drafts (enforced by RLS)
          const { data } = await (supabase as any)
            .from("white_label_brands").select("subdomain, organization_id, allow_indexing, draft")
            .eq("subdomain", target.subdomain).maybeSingle();
          if (data) brand = { subdomain: data.subdomain, organization_id: data.organization_id, allow_indexing: data.allow_indexing, config: data.draft || {} };
        }
        if (!brand) {
          const { data } = await (supabase as any).rpc("get_public_brand", { _subdomain: target.subdomain });
          if (data) brand = data as Brand;
        }
      } catch (e) {
        console.warn("[Brand] lookup failed, using default brand", e);
      }
      if (cancelled) return;
      if (brand) applyBrand(brand.config || {});
      else resetBrand();
      setState({ brand, isWhiteLabel: !!brand, isPreview: target.isPreview, loading: false });
    })();
    return () => {
      cancelled = true;
      resetBrand();
    };
  }, [target]);

  if (state.loading) return <div className="min-h-screen bg-background" />;
  return <BrandContext.Provider value={state}>{children}</BrandContext.Provider>;
}
